import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShieldAlt,
  faHandshake,
  faUserTie,
} from "@fortawesome/free-solid-svg-icons";
import { Container, Typography, Box, Grid } from "@mui/material";
import { useTranslation } from "react-i18next";

// Assets (Keep your existing imports)
import logoImage from "../assets/logo.jpg";
import asosiasiImage from "../assets/asosiasi.jpg";
import headerBg from "../assets/about.jpg";

const Tentang = () => {
  const { t } = useTranslation();

  const values = t("tentang.values", { returnObjects: true }) || [];
  return (
    <Box sx={{ bgcolor: "#ffffff", overflow: "hidden" }}>

      {/* ── 1. HERO SECTION ── */}
      <Box sx={{
        position: "relative",
        height: { xs: "62vh", md: "72vh" },
        minHeight: { xs: 440, md: 540 },
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
        overflow: "hidden",
      }}>
        {/* Background image */}
        <Box sx={{
          position: "absolute", inset: 0,
          backgroundImage: `url(${headerBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          transform: "scale(1.04)",
        }} />

        {/* Layered gradient overlay */}
        <Box sx={{
          position: "absolute", inset: 0,
          background: "linear-gradient(110deg, rgba(5,10,30,0.90) 0%, rgba(5,10,30,0.68) 55%, rgba(5,10,30,0.28) 100%)",
        }} />

        {/* Subtle grid texture */}
        <Box sx={{
          position: "absolute", inset: 0,
          backgroundImage: "linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
        }} />

        {/* Decorative vertical accent lines on right */}
        {[18, 26].map((right, i) => (
          <Box key={i} sx={{
            position: "absolute",
            right: `${right}%`,
            top: "15%",
            width: "1px",
            height: "55%",
            background: "linear-gradient(to bottom, transparent, rgba(255,255,255,0.12), transparent)",
            display: { xs: "none", md: "block" },
          }} />
        ))}

        {/* Bottom fade into white */}
        <Box sx={{
          position: "absolute", bottom: 0, left: 0, right: 0, height: 120,
          background: "linear-gradient(to bottom, transparent, #ffffff)",
        }} />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1, pb: { xs: 7, md: 9 } }}>
          {/* Company pill badge */}
          <Box sx={{
            display: "inline-flex", alignItems: "center", gap: 1,
            bgcolor: "rgba(249,115,22,0.14)",
            border: "1px solid rgba(249,115,22,0.38)",
            px: 2, py: 0.8, borderRadius: "100px", mb: 3,
          }}>
            <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#f97316", boxShadow: "0 0 8px rgba(249,115,22,0.8)" }} />
            <Typography sx={{ color: "#fb923c", fontSize: "0.72rem", fontWeight: 700, letterSpacing: 2, textTransform: "uppercase" }}>
              CV. Mulia Utama Mandiri
            </Typography>
          </Box>

          {/* Main title */}
          <Typography sx={{
            fontWeight: 900,
            color: "white",
            fontSize: { xs: "3rem", md: "5.5rem" },
            lineHeight: 1,
            letterSpacing: "-0.04em",
            mb: 2.5,
          }}>
            {t("tentang.title")}
          </Typography>

          {/* Accent divider */}
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Box sx={{ width: 56, height: 3, bgcolor: "#f97316", borderRadius: 2 }} />
            <Box sx={{ width: 18, height: 3, bgcolor: "rgba(255,255,255,0.25)", borderRadius: 2 }} />
          </Box>
        </Container>
      </Box>

      {/* ── 2. BRAND & STORY SECTION ── */}
      <Container maxWidth="lg" sx={{ pt: { xs: 2, md: 4 }, pb: { xs: 8, md: 12 } }}>
        <Grid container spacing={{ xs: 6, md: 10 }} alignItems="center" flexDirection={{ xs: "column-reverse", md: "row" }}>

          {/* Left: Text Content */}
          <Grid item xs={12} md={6}>
            {/* Eyebrow label */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
              <Box sx={{ width: 28, height: 2, bgcolor: "#f97316", borderRadius: 1 }} />
              <Typography sx={{ color: "#f97316", fontWeight: 700, fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: 2.5 }}>
                Tentang Perusahaan
              </Typography>
            </Box>

            {/* Heading */}
            <Typography variant="h3" sx={{
              fontWeight: 800,
              color: "#0f172a",
              mb: 3,
              lineHeight: 1.15,
              letterSpacing: "-0.03em",
              fontSize: { xs: "1.9rem", md: "2.5rem" },
            }}>
              {t("hero.title")}
            </Typography>

            {/* Description */}
            <Typography variant="body1" sx={{
              color: "#64748b",
              lineHeight: 1.9,
              fontSize: "1.05rem",
              mb: 5,
            }}>
              {t("tentang.description")}
            </Typography>
          </Grid>

          {/* Right: Logo Display */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", position: "relative", py: 4 }}>
              {/* Animated background blob */}
              <Box sx={{
                position: "absolute",
                width: "80%",
                height: "80%",
                borderRadius: "42% 58% 55% 45% / 42% 38% 62% 58%",
                background: "linear-gradient(135deg, #fef3c7 0%, #fed7aa 100%)",
                animation: "blobMorph 14s ease-in-out infinite",
                "@keyframes blobMorph": {
                  "0%, 100%": { borderRadius: "42% 58% 55% 45% / 42% 38% 62% 58%" },
                  "50%": { borderRadius: "60% 40% 38% 62% / 60% 58% 42% 40%" },
                },
              }} />

              {/* Floating dot accents */}
              <Box sx={{ position: "absolute", top: "8%", right: "8%", width: 44, height: 44, borderRadius: "50%", bgcolor: "#f97316", opacity: 0.15 }} />
              <Box sx={{ position: "absolute", bottom: "8%", left: "5%", width: 22, height: 22, borderRadius: "50%", bgcolor: "#2563eb", opacity: 0.15 }} />
              <Box sx={{ position: "absolute", top: "40%", left: "2%", width: 12, height: 12, borderRadius: "50%", bgcolor: "#f97316", opacity: 0.25 }} />

              {/* Logo card */}
              <Box sx={{
                position: "relative",
                zIndex: 2,
                bgcolor: "white",
                borderRadius: "28px",
                boxShadow: "0 24px 60px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.04)",
                p: { xs: 3.5, md: 5 },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}>
                <img src={logoImage} alt="CV. Mulia Utama Mandiri" style={{ width: "100%", maxWidth: 260, display: "block" }} />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* ── 3. ASSOCIATION SECTION ── */}
      <Box sx={{
        py: { xs: 9, md: 11 },
        background: "linear-gradient(135deg, #1e1b4b 0%, #2e1065 50%, #4a1d96 100%)",
        position: "relative",
        overflow: "hidden",
      }}>
        {/* Dot texture */}
        <Box sx={{
          position: "absolute", inset: 0,
          backgroundImage: "radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)",
          backgroundSize: "26px 26px",
        }} />

        {/* Ambient glow spheres */}
        <Box sx={{
          position: "absolute", right: "-8%", top: "-40%",
          width: "45%", paddingBottom: "45%",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(139,92,246,0.25) 0%, transparent 70%)",
        }} />
        <Box sx={{
          position: "absolute", left: "-8%", bottom: "-40%",
          width: "35%", paddingBottom: "35%",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(109,75,148,0.2) 0%, transparent 70%)",
        }} />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Grid container spacing={{ xs: 6, md: 8 }} alignItems="center">

            {/* Logo side */}
            <Grid item xs={12} md={4} sx={{ display: "flex", justifyContent: { xs: "center", md: "flex-start" } }}>
              <Box sx={{ position: "relative", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                {/* Concentric ring decorations */}
                {[200, 160, 124].map((size, i) => (
                  <Box key={i} sx={{
                    position: "absolute",
                    width: size, height: size,
                    borderRadius: "50%",
                    border: `1px solid rgba(255,255,255,${0.1 - i * 0.025})`,
                  }} />
                ))}

                {/* Logo circle */}
                <Box sx={{
                  width: 120, height: 120,
                  bgcolor: "rgba(255,255,255,0.96)",
                  borderRadius: "50%",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  p: 2,
                  boxShadow: "0 0 0 6px rgba(255,255,255,0.08), 0 24px 48px rgba(0,0,0,0.35)",
                  position: "relative", zIndex: 1,
                }}>
                  <img src={asosiasiImage} alt="Asosiasi" style={{ width: "100%", height: "100%", objectFit: "contain", borderRadius: "50%" }} />
                </Box>
              </Box>
            </Grid>

            {/* Text side */}
            <Grid item xs={12} md={8} sx={{ textAlign: { xs: "center", md: "left" } }}>
              {/* Badge chip */}
              <Box sx={{
                display: "inline-flex", alignItems: "center", gap: 1,
                bgcolor: "rgba(167,139,250,0.15)",
                border: "1px solid rgba(167,139,250,0.3)",
                px: 2, py: 0.8, borderRadius: "100px", mb: 3,
              }}>
                <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#a78bfa", boxShadow: "0 0 8px rgba(167,139,250,0.8)" }} />
                <Typography sx={{ color: "#c4b5fd", fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: 2 }}>
                  {t("tentang.affiliation.title")}
                </Typography>
              </Box>

              {/* Heading */}
              <Typography variant="h3" sx={{
                fontWeight: 800,
                color: "white",
                mb: 2.5,
                fontSize: { xs: "1.8rem", md: "2.4rem" },
                letterSpacing: "-0.025em",
                lineHeight: 1.2,
              }}>
                {t("tentang.association")}
              </Typography>

              {/* Accent divider */}
              <Box sx={{
                display: "flex", gap: 1, mb: 3,
                justifyContent: { xs: "center", md: "flex-start" },
              }}>
                <Box sx={{ width: 40, height: 3, bgcolor: "#a78bfa", borderRadius: 1 }} />
                <Box sx={{ width: 16, height: 3, bgcolor: "rgba(255,255,255,0.18)", borderRadius: 1 }} />
              </Box>

              {/* Description */}
              <Typography variant="body1" sx={{
                color: "rgba(255,255,255,0.68)",
                lineHeight: 1.9,
                fontSize: "1rem",
                maxWidth: 560,
              }}>
                {t("tentang.affiliation.description")}
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* 4. VALUES SECTION */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: "#f8fafc", position: "relative", overflow: "hidden" }}>
        {/* Dot pattern background */}
        <Box sx={{
          position: "absolute", inset: 0,
          backgroundImage: "radial-gradient(rgba(37,99,235,0.05) 1px, transparent 1px)",
          backgroundSize: "24px 24px",
          pointerEvents: "none",
        }} />

        <Container maxWidth="lg" sx={{ position: "relative" }}>
          {/* Section header — editorial split layout */}
          <Grid container spacing={{ xs: 4, md: 8 }} alignItems="flex-end" sx={{ mb: { xs: 6, md: 9 } }}>
            {/* Left: label + big heading */}
            <Grid item xs={12} md={6}>
              <Typography sx={{
                color: "#2563eb", fontWeight: 700, fontSize: "0.72rem",
                textTransform: "uppercase", letterSpacing: 2.5, mb: 2,
                display: "flex", alignItems: "center", gap: 1,
              }}>
                <Box component="span" sx={{ display: "inline-block", width: 24, height: 2, bgcolor: "#2563eb", borderRadius: 1 }} />
                {t("coreValues.title")}
              </Typography>
              <Typography sx={{
                fontWeight: 900,
                fontSize: { xs: "2.4rem", md: "3.2rem" },
                lineHeight: 1.05,
                letterSpacing: "-0.04em",
                color: "#0f172a",
              }}>
                {t("tentang.valuesTitle").split(" ").slice(0, -1).join(" ")}{" "}
                <Box component="span" sx={{
                  background: "linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}>
                  {t("tentang.valuesTitle").split(" ").slice(-1)[0]}
                </Box>
              </Typography>
              {/* Underline accent */}
              <Box sx={{ mt: 2.5, display: "flex", gap: 0.75 }}>
                <Box sx={{ height: 4, width: 40, bgcolor: "#2563eb", borderRadius: 2 }} />
                <Box sx={{ height: 4, width: 16, bgcolor: "#f97316", borderRadius: 2 }} />
                <Box sx={{ height: 4, width: 8, bgcolor: "#059669", borderRadius: 2 }} />
              </Box>
            </Grid>

            {/* Right: subtitle + small stat strip */}
            <Grid item xs={12} md={6}>
              <Box sx={{ borderLeft: { md: "2px solid #e2e8f0" }, pl: { md: 5 } }}>
                <Typography variant="body1" sx={{
                  color: "#64748b", lineHeight: 1.8, fontSize: "1rem", mb: 3,
                }}>
                  {t("coreValues.subtitle")}
                </Typography>
                <Box sx={{
                  display: "inline-flex", alignItems: "center", gap: 1.5,
                  px: 2.5, py: 1.25, borderRadius: 2,
                  bgcolor: "#fff", border: "1px solid #e2e8f0",
                  boxShadow: "0 1px 6px rgba(0,0,0,0.05)",
                }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#22c55e", boxShadow: "0 0 8px rgba(34,197,94,0.6)" }} />
                  <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: "#0f172a" }}>
                    {values.length} {t("coreValues.countLabel")}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>

          {/* Value cards — colored-header split style */}
          <Grid container spacing={3}>
            {values.map((item, i) => {
              const palette = [
                { color: "#2563eb", grad: "linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)", shadow: "rgba(37,99,235,0.28)" },
                { color: "#f97316", grad: "linear-gradient(135deg, #c2410c 0%, #fb923c 100%)", shadow: "rgba(249,115,22,0.28)" },
                { color: "#059669", grad: "linear-gradient(135deg, #065f46 0%, #34d399 100%)", shadow: "rgba(5,150,105,0.28)" },
              ];
              const icons = [faShieldAlt, faHandshake, faUserTie];
              const { color, grad, shadow } = palette[i];

              return (
                <Grid item xs={12} md={4} key={i}>
                  <Box sx={{
                    borderRadius: "24px",
                    overflow: "hidden",
                    bgcolor: "#fff",
                    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: `0 28px 48px -12px ${shadow}`,
                    },
                  }}>
                    {/* Colored header zone */}
                    <Box sx={{
                      background: grad,
                      px: 4, pt: 4, pb: 5,
                      position: "relative",
                      overflow: "hidden",
                    }}>
                      {/* Subtle dot pattern inside header */}
                      <Box sx={{
                        position: "absolute", inset: 0,
                        backgroundImage: "radial-gradient(rgba(255,255,255,0.12) 1px, transparent 1px)",
                        backgroundSize: "18px 18px",
                        pointerEvents: "none",
                      }} />
                      {/* Large faded icon watermark */}
                      <Box sx={{
                        position: "absolute", right: -12, bottom: -16,
                        fontSize: "7rem", opacity: 0.12, color: "#fff",
                        lineHeight: 1, pointerEvents: "none",
                      }}>
                        <FontAwesomeIcon icon={icons[i]} />
                      </Box>
                      {/* Icon badge */}
                      <Box sx={{
                        width: 56, height: 56, borderRadius: "16px",
                        bgcolor: "rgba(255,255,255,0.2)",
                        border: "1.5px solid rgba(255,255,255,0.35)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        position: "relative", zIndex: 1,
                      }}>
                        <FontAwesomeIcon icon={icons[i]} style={{ color: "#fff", fontSize: "1.4rem" }} />
                      </Box>
                    </Box>

                    {/* White content zone */}
                    <Box sx={{ p: 4, flexGrow: 1, display: "flex", flexDirection: "column" }}>
                      <Typography variant="h5" sx={{
                        fontWeight: 800, color: "#0f172a", mb: 1.5,
                        letterSpacing: "-0.02em", lineHeight: 1.2,
                      }}>
                        {item.title}
                      </Typography>
                      <Box sx={{ width: 28, height: 3, bgcolor: color, borderRadius: 1, mb: 2.5 }} />
                      <Typography variant="body2" sx={{ color: "#64748b", lineHeight: 1.85, fontSize: "0.92rem" }}>
                        {item.description}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default Tentang;